// The world is a RASTERISED GRID: an infinite field of TILE×TILE cells, each a packed
// tile (base material + modifier, or AIR), generated as a PURE function of (seed,
// generator, tileX, tileY). Server and every client sample identical terrain — nothing
// streamed, nothing stored beyond a per-generator chunk cache.
//
// A GENERATOR is an instance (createGenerator), created per world, owning its own caches
// and implementing one named terrain style. The chunk-assembly pipeline is shared across
// generators; only the per-tile BASE decision (is this cell solid, and of what material)
// varies by name — see the GENERATORS table. Today there's one, "original".
//
// Layers, applied in order when a chunk is generated:
//   1. BASE     the generator's per-tile decision (original: anisotropic Perlin noise,
//               engine/noise.js, sampled so solids form long horizontal streaks).
//   2. FEATURE  a rare per-chunk override of the whole generation: `void` (a gap in the
//               sky, no tiles) or `fortress` (a rigid lattice of rooms).
//   3. STRUCTURE a rare stamp of a fixed shape with guaranteed loot: `sanctuary`.
//
// A chunk is the unit of caching and of the feature/structure rolls; tiles within it come
// from whichever layer wins. Structures stay inside one chunk, so a tile's solidity
// depends only on its own chunk — no neighbour lookups to generate.
//
// Vertical layout: infinite UP (negative y), and below VOID_Y there are no tiles at all —
// that's the void, where falling drains HP.

import WORLD from "../../data/world.json" with { type: "json" };
import { FortressFeature } from "../content/features/FortressFeature.js";
import { makeNoise } from "./noise.js";
import { AIR, BIOME_BASE, BIOMES, BIOME_NOISE, isSolidTile, isDoor, withModifier, parseToken } from "./tiles.js";

export const TILE = WORLD.tile;
export const VOID_Y = WORLD.voidY;
export const SPAWN = WORLD.spawn;
export const WORLD_SEED = WORLD.seed;

// Floor modes (data/world.json): what the bottom of the world is. `bounded` says whether
// terrain stops at VOID_Y (a void below) or generation continues down forever; `damage`
// is the HP/s an entity below the line bleeds. "lava" (bounded, burns) is today's world
// and the default; "none" (unbounded, harmless) is an infinite world with no bottom. The
// floor rides the `welcome` message so client and server build the same vertical world.
export const FLOORS = WORLD.floors;
export const DEFAULT_FLOOR = WORLD.floor;
export const floorBounded = (name) => (FLOORS[name] || FLOORS[DEFAULT_FLOOR]).bounded;

const CTW = WORLD.chunkTiles.w;   // chunk width, in tiles
const CTH = WORLD.chunkTiles.h;
export const CHUNK_W = CTW * TILE;
export const CHUNK_H = CTH * TILE;

const BIOME = WORLD.biome;
const FEAT = WORLD.features;
const STRUCT = WORLD.structures;

// --- coordinate helpers (generator-independent) ------------------------------
export const tileX = (px) => Math.floor(px / TILE);
export const tileY = (py) => Math.floor(py / TILE);
export const chunkX = (px) => Math.floor(px / CHUNK_W);
export const chunkY = (py) => Math.floor(py / CHUNK_H);
const chunkOfTileX = (tcx) => Math.floor(tcx / CTW);
const chunkOfTileY = (tri) => Math.floor(tri / CTH);

// --- seeded streams (pure functions of the seed) -----------------------------
function chunkHash(seed, cx, cy) {
    let h = seed >>> 0;
    h = Math.imul(h ^ (cx | 0), 0x9e3779b1) >>> 0;
    h = Math.imul(h ^ (cy | 0), 0x85ebca6b) >>> 0;
    h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
    return (h ^ (h >>> 16)) >>> 0;
}
function mulberry(a) {
    return function () {
        a |= 0; a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

// A deterministic stream for one chunk; `salt` gives each decorator (pickups, features,
// structures) its own stream so they don't perturb each other.
export function chunkRng(seed, cx, cy, salt = 0) {
    return mulberry(chunkHash(seed ^ salt, cx, cy));
}

const FEAT_SALT = 0x0000f00d;
const STRUCT_SALT = 0x00057acc;
const DOOR_SALT = 0x00d00d15;
const SPAWN_SALT = 0x0059a10f;
const BIOME_SALT = 0x00b10123;

// Whether a generated door starts open — a deterministic ~15% roll (world.json's
// features.doorOpenChance) keyed on the door's position, so server and client agree and a
// re-visited door reads the same. Callers key a whole multi-tile doorway on one (kx, ky)
// so its tiles share a single roll (no half-open doorways).
function doorOpenAt(seed, kx, ky) {
    return (chunkHash(seed ^ DOOR_SALT, kx, ky) >>> 0) / 4294967296 < FEAT.doorOpenChance;
}

const SPAWN_CX = chunkX(SPAWN.x);
const SPAWN_CY = chunkY(SPAWN.y);
const SPAWN_BIOME_CHUNKS = 1; // chunks around spawn kept in biome 0

// Spawn-structure shapes, drawn as ASCII art (bottom row is the floor). `X` is the
// biome's base tile, `W` is wood, `D` is an open door, `.` is air. The middle column
// stays clear for the bottom few rows so a player materialises inside with room to stand.
// Index 0 is the plain pad the canonical spawn uses; the rest are picked at random for
// the scattered spawns, so a respawn point is a distinct little build — a lodge, a
// battlemented keep, a raised watch-post, a bunker — not a bare tile.
const SPAWN_TOKENS = { W: "tile:wood", D: "tile:wood mod:doorOpen" };
const SPAWN_TEMPLATES = [
    // 0 — landing pad (the canonical spawn keeps this; its gate is added separately)
    [
        ".............",
        ".............",
        ".............",
        "XXXXXXXXXXXXX",
    ],
    // 1 — a timber lodge with a pitched roof and doors on both flanks
    [
        ".....WWW.....",
        "...WWWWWWW...",
        "...W.....W...",
        "...W.....W...",
        "...D.....D...",
        "XXXXXXXXXXXXX",
    ],
    // 2 — a stone keep: battlements over a roofed hall with side gates
    [
        "X.X.X.X.X.X.X",
        "XXXXXXXXXXXXX",
        "X...........X",
        "X...........X",
        "D...........D",
        "XXXXXXXXXXXXX",
    ],
    // 3 — a watch-post: a raised wooden deck on posts over an open floor
    [
        "....W...W....",
        "....W...W....",
        "....WWWWW....",
        ".............",
        ".............",
        "D...........D",
        "XXXXXXXXXXXXX",
    ],
    // 4 — a bunker: a solid roof, side doors, and a stepped base
    [
        "XXXXXXXXXXXXX",
        "X...........X",
        "X...........X",
        "D...........D",
        "XX.........XX",
        "XXXXXXXXXXXXX",
    ],
];

// The sanctuary: a WOOD-framed room set into the biome terrain, entered through a door,
// with guaranteed loot on its floor. `X` is the biome's appropriate base tile, `.` is
// air, every other letter comes from `mapping`. Multiple weapons cycle across the loot
// markers that share a token, so a sanctuary is worth crossing the map for.
const SANCTUARY = {
    w: 9,
    h: 5,
    rows: [
        "WWWWWWWWW",
        "W.......W",
        "D.......W",
        "WA.G.G.HW",
        "XXXXXXXXX",
    ],
    mapping: {
        W: "tile:wood",
        D: "tile:wood mod:doorOpen",   // a wooden door — starts open, so it's the way in
        A: "loot:armor",
        H: "loot:health",
        G: ["loot:shoop", "loot:stonks"],
    },
};

// The whole-chunk feature overrides. Stateless and shared across generators.
const FEATURES = {};
FEATURES.fortress = new FortressFeature();
FEATURES.void = {};

// --- generator strategies ----------------------------------------------------
// A named generator supplies the BASE layer: `baseTileAt(g, tcx, tri)` returns the packed
// tile for a "normal" cell (or AIR), and `fillTileAt(g, tcx, tri)` the material a
// built/solidified cell and a spawn-structure `X` take. `g` is the generator's own helper
// surface (noise, biome lookups). The chunk assembly below is shared.

// One Ground materials, packed once.
const DIRT = parseToken("tile:dirt").tile;
const GRASS = parseToken("tile:grass").tile;
const WATER = parseToken("tile:water").tile;

// One Ground tuning. Its world has a single hilly GROUND: a grass surface near the bottom
// (well below where players spawn, so they descend to it), dirt underneath carved by caves,
// and sparse floating platforms filling the sky above. All three layers read the seed's one
// noise field at different scales/offsets so they don't line up.
const ONE_GROUND = {
    base: -38,         // baseline surface row (tri) — just below the lowest spawn (~-47), so
                       // players always descend TO the ground and never spawn buried; deep
                       // dirt runs from here down to the void.
    hillAmp: 5,        // hills rise/fall this many tiles
    hillScale: 620,    // px — how wide the hills roll
    crust: 2,          // solid rows under the grass before caves may start
    caveScale: 170,    // px — cave size
    caveThreshold: 0.34, // cave (air) where the cave field exceeds this
    skyScale: 500,     // px — floating-platform streaks (like the original, but rarer)
    skyScaleY: 210,
    skyThreshold: 0.34, // platform where the sky field exceeds this (higher = sparser)
    lakeScale: 900,    // px — how wide the lake regions spread
    lakeThreshold: 0.30, // a lake where the (own) lake field exceeds this — higher = rarer (fbm peaks near 0.5)
    lakeDepth: 6,      // the deepest a lake basin sinks below the surface line (tiles)
};

const GENERATORS = {
    original: {
        // Anisotropic fbm threshold: the X scale is far larger than Y (world.json), so
        // the noise stretches horizontally into long streaky platforms, not blobs.
        baseTileAt(g, tcx, tri) {
            const px = (tcx + 0.5) * TILE, py = (tri + 0.5) * TILE;
            const solid = g.noise().fbm(px / BIOME.noise.scaleX, py / BIOME.noise.scaleY, BIOME.noise.octaves) > BIOME.noise.threshold;
            return solid ? g.biomeBaseAt(tcx, tri) : AIR;
        },
        fillTileAt(g, tcx, tri) { return g.biomeBaseAt(tcx, tri); },
    },

    "one ground": {
        // No void chunks: a gap in the sky is jarring in a world that's meant to read as
        // one continuous ground — those chunks generate as normal terrain instead.
        allowVoid: false,

        // The grass surface row for a column — a smooth 1D heightmap (the noise sampled
        // along x at a fixed line), so the ground rolls in hills.
        surface(g, tcx) {
            const px = (tcx + 0.5) * TILE;
            const h = g.noise().fbm(px / ONE_GROUND.hillScale, 1000.5, 2); // fixed y = a 1D slice
            return Math.round(ONE_GROUND.base + h * ONE_GROUND.hillAmp);
        },
        // A lake basin sunk into the ground at this column, or null. Its own low-frequency
        // noise field decides where lakes sit; the field's strength past the threshold sets
        // how deep the bowl cuts, so a lake tapers from shallow rims to a deep middle. Water
        // fills rows [surface .. bed]; below `bed` is the sealed dirt basin floor. A pure
        // function of the seed, like every other layer — server and client agree with no sync.
        lake(g, tcx) {
            const px = (tcx + 0.5) * TILE;
            const lf = g.noise().fbm(px / ONE_GROUND.lakeScale, 4200.5, 2); // own y-slice, offset from the others
            if (lf <= ONE_GROUND.lakeThreshold) return null;
            const s = Math.min(1, (lf - ONE_GROUND.lakeThreshold) / (0.5 - ONE_GROUND.lakeThreshold)); // 0..1 depth (fbm peaks ~0.5)
            const top = this.surface(g, tcx);
            return { top, bed: top + Math.max(1, Math.round(s * ONE_GROUND.lakeDepth)) };
        },
        solid(g, tcx, tri) {
            const surface = this.surface(g, tcx);
            const lake = this.lake(g, tcx);
            if (lake) {
                if (tri >= lake.top && tri <= lake.bed) return false;                 // the water body — non-solid
                if (tri > lake.bed && tri <= lake.bed + ONE_GROUND.crust) return true; // sealed basin floor (no cave drains)
            }
            const px = (tcx + 0.5) * TILE, py = (tri + 0.5) * TILE;
            if (tri < surface) {
                // Sky: sparse floating platforms — anisotropic streaks like the original,
                // rarer (a higher threshold) and offset so they don't echo the caves.
                return g.noise().fbm(px / ONE_GROUND.skyScale, py / ONE_GROUND.skyScaleY + 500, 2) > ONE_GROUND.skyThreshold;
            }
            if (tri <= surface + ONE_GROUND.crust) return true; // the grass + a solid crust
            // Underground: dirt, minus caves carved by a rounder noise field.
            return g.noise().fbm(px / ONE_GROUND.caveScale, py / ONE_GROUND.caveScale + 900, 3) <= ONE_GROUND.caveThreshold;
        },
        baseTileAt(g, tcx, tri) {
            // Water fills the lake basin down to its bed — a non-solid tile the renderer still draws.
            const lake = this.lake(g, tcx);
            if (lake && tri >= lake.top && tri <= lake.bed) return WATER;
            if (!this.solid(g, tcx, tri)) return AIR;
            // Grass caps a top exposed to the OPEN SKY — the surface and every floating
            // island (tri at or above the surface line). A cave floor deep underground is
            // exposed too, but to a pocket, not the sky, so it stays plain dirt. A lake's
            // shore (solid next to water) reads as dirt, not grass — a muddy bank.
            const exposed = !this.solid(g, tcx, tri - 1);
            return exposed && tri <= this.surface(g, tcx) && !lake ? GRASS : DIRT;
        },
        fillTileAt() { return DIRT; },
    },
};

export const generatorNames = () => Object.keys(GENERATORS);
export const isGenerator = (name) => Object.prototype.hasOwnProperty.call(GENERATORS, name);

// --- a Generator instance ----------------------------------------------------
// One per world (seed × name), owning all of its caches. Two worlds on the same seed and
// generator can share one instance (the module wrappers do); a different generator gets
// its own instance with its own caches, so nothing collides.
export function createGenerator(seed, name = "original", { bounded = true } = {}) {
    const strat = GENERATORS[name] || GENERATORS.original;

    // Instance caches (were module globals keyed by seed; now they just live here).
    let _noise = null;
    let _biomeNoise = null;
    let _cuts = null;
    let _spawns = null;
    const gridCache = new Map();

    const noise = () => (_noise ||= makeNoise(seed));
    const biomeNoise = () => (_biomeNoise ||= makeNoise((seed ^ BIOME_SALT) >>> 0));

    // Cut points that split the biome noise into EQUAL-AREA bands. Perlin/fbm values
    // cluster around the middle, so slicing the raw range evenly would make outer biomes
    // vanishingly rare. Sample the field once, sort, cut at k/N quantiles.
    const biomeCuts = () => {
        if (_cuts) return _cuts;
        const n = biomeNoise();
        const vals = [];
        for (let gx = 0; gx < 64; gx++) {
            for (let gy = 0; gy < 64; gy++) {
                const x = (gx - 32) * 71, y = (gy - 32) * 57; // spread across the field, coprime steps
                vals.push(n.fbm(x / BIOME_NOISE.scale, y / BIOME_NOISE.scale, BIOME_NOISE.octaves));
            }
        }
        vals.sort((a, b) => a - b);
        _cuts = [];
        for (let k = 1; k < BIOMES.length; k++) _cuts.push(vals[Math.floor((vals.length * k) / BIOMES.length)]);
        return _cuts;
    };

    const biomeIndexAt = (tcx, tri) => {
        const cx = chunkOfTileX(tcx), cy = chunkOfTileY(tri);
        if (Math.abs(cx - SPAWN_CX) <= SPAWN_BIOME_CHUNKS && Math.abs(cy - SPAWN_CY) <= SPAWN_BIOME_CHUNKS) return 0;
        const v = biomeNoise().fbm(tcx / BIOME_NOISE.scale, tri / BIOME_NOISE.scale, BIOME_NOISE.octaves);
        const cuts = biomeCuts();
        let i = 0;
        while (i < cuts.length && v >= cuts[i]) i++;
        return i;
    };

    // The packed base tile for a location's biome — what a "solid" cell there becomes.
    const biomeBaseAt = (tcx, tri) => BIOMES[biomeIndexAt(tcx, tri)].baseTile;

    // The helper surface handed to the generator strategy.
    const g = { seed, noise, biomeBaseAt, biomeIndexAt };

    // The spawn STRUCTURES: a handful of safe platforms scattered SEVERAL CHUNKS APART
    // around spawn (count / spreadChunks from world.json). Index 0 is the canonical SPAWN
    // (a plain pad, keeping its starting gate); the rest ring out a few chunks and pick a
    // random template. Pure function of the seed, memoised, so generator and picker agree.
    const spawnPoints = () => {
        if (_spawns) return _spawns;
        const nCount = Math.max(1, WORLD.spawnPad.count | 0);
        const spread = Math.max(2, (WORLD.spawnPad.spreadChunks | 0) || 3);
        const rng = chunkRng(seed, SPAWN_CX, SPAWN_CY, SPAWN_SALT);
        const t0x = SPAWN_CX * CTW, t0y = SPAWN_CY * CTH;
        const pts = [{
            x: SPAWN.x, y: SPAWN.y, cx: SPAWN_CX, cy: SPAWN_CY, tmpl: 0,
            col: tileX(SPAWN.x) - t0x, row: tileY(SPAWN.y + 2 * TILE) - t0y,
        }];
        for (let i = 1; i < nCount; i++) {
            const side = i % 2 === 1 ? 1 : -1;
            const step = Math.ceil(i / 2) - 1; // 0,0,1,1,2,...
            const cx = SPAWN_CX + side * (spread + 3 * step + Math.floor(rng() * 2));
            const cy = SPAWN_CY + (Math.floor(rng() * 6) - 3); // -3..+2 chunks, all above the void
            const tmpl = 1 + Math.floor(rng() * (SPAWN_TEMPLATES.length - 1));
            const col = (CTW >> 1) + Math.round((rng() - 0.5) * 4); // near chunk centre, a touch of jitter
            const row = CTH >> 1;
            pts.push({ x: (cx * CTW + col + 0.5) * TILE, y: (cy * CTH + row) * TILE - 2 * TILE, cx, cy, tmpl, col, row });
        }
        _spawns = pts;
        return pts;
    };

    const hostsSpawn = (cx, cy) => spawnPoints().some((e) => e.cx === cx && e.cy === cy);

    // Stamp a spawn structure's template into its chunk grid, and clear a drop lane above
    // it so a player lands in the structure rather than in whatever noise sits overhead.
    const stampSpawnStructure = (grid, e, t0x, t0y) => {
        const tmpl = SPAWN_TEMPLATES[e.tmpl] || SPAWN_TEMPLATES[0];
        const w = tmpl[0].length, h = tmpl.length;
        const floorTri = e.cy * CTH + e.row;
        const topTri = floorTri - (h - 1);
        const originCol = e.cx * CTW + e.col - (w >> 1);
        for (let r = 0; r < h; r++) {
            for (let c = 0; c < w; c++) {
                const lc = originCol + c - t0x, lr = topTri + r - t0y;
                if (lc < 0 || lc >= CTW || lr < 0 || lr >= CTH) continue;
                const ch = tmpl[r][c];
                let tile = AIR;
                if (ch === "X") tile = strat.fillTileAt(g, originCol + c, topTri + r);
                else if (ch !== ".") { const p = parseToken(SPAWN_TOKENS[ch]); if (p && p.tile != null) tile = p.tile; }
                grid[lr * CTW + lc] = tile;
            }
        }
        const dropLc = e.cx * CTW + e.col - t0x;
        for (let up = 1; up <= 5; up++) {
            const lr = topTri - up - t0y;
            if (dropLc >= 0 && dropLc < CTW && lr >= 0 && lr < CTH) grid[lr * CTW + dropLc] = AIR;
        }
    };

    // Which whole-chunk generation mode this chunk uses. Any chunk holding a spawn
    // structure is forced normal, so a spawn is never marooned in a void or a fortress.
    const featureOf = (cx, cy) => {
        if (hostsSpawn(cx, cy)) return "normal";
        const r = chunkRng(seed, cx, cy, FEAT_SALT)();
        if (strat.allowVoid !== false && r < FEAT.void) return "void"; // a generator may forbid voids
        if (r >= FEAT.void && r < FEAT.void + FEAT.fortress) return "fortress"; // (unchanged band; a forbidden void reads as normal)
        return "normal";
    };

    // The structure sitting in this chunk, or null. Structures go only in normal chunks,
    // at a fixed offset so they never straddle a chunk boundary.
    const structureOf = (cx, cy) => {
        if (hostsSpawn(cx, cy)) return null;
        if (featureOf(cx, cy) !== "normal") return null;
        const r = chunkRng(seed, cx, cy, STRUCT_SALT)();
        if (r >= STRUCT.sanctuary) return null;
        const def = SANCTUARY;
        return {
            kind: "sanctuary",
            col: Math.floor((CTW - def.w) / 2),
            row: Math.floor((CTH - def.h) / 2),
            def,
        };
    };

    const generateGrid = (cx, cy) => {
        const gkey = cx + ":" + cy;
        const hit = gridCache.get(gkey);
        if (hit) return hit;

        // Each cell is a PACKED TILE (engine/tiles.js): base material in the low nibble,
        // modifier in the high nibble. 0 = air.
        const grid = new Uint8Array(CTW * CTH);
        const feature = featureOf(cx, cy);
        const t0x = cx * CTW, t0y = cy * CTH;

        if (feature !== "void") {
            for (let row = 0; row < CTH; row++) {
                const tri = t0y + row;
                if (bounded && tri * TILE >= VOID_Y) continue; // below the void line: no tiles (bounded floors only)
                for (let col = 0; col < CTW; col++) {
                    const tcx = t0x + col;

                    if (feature !== "normal") {
                        const maybeTile = FEATURES[feature].getTile({
                            getBiomeBaseTile: () => biomeBaseAt(tcx, tri),
                            doorOpen: (kx, ky) => doorOpenAt(seed, kx, ky),
                        }, tcx, tri);
                        if (maybeTile) grid[row * CTW + col] = maybeTile;
                        continue;
                    }

                    const tile = strat.baseTileAt(g, tcx, tri);
                    if (tile) grid[row * CTW + col] = tile;
                }
            }
        }

        // Stamp a structure over the base, from its legend.
        const structure = structureOf(cx, cy);
        if (structure) {
            const { col: sc, row: sr, def } = structure;
            for (let r = 0; r < def.h; r++) {
                for (let c = 0; c < def.w; c++) {
                    const ch = def.rows[r][c];
                    const tcx = t0x + sc + c, tri = t0y + sr + r;
                    let tile = AIR;
                    if (ch === "X") tile = strat.fillTileAt(g, tcx, tri); // generator-appropriate base
                    else if (ch !== ".") {
                        const tokens = def.mapping[ch];
                        const parsed = parseToken(Array.isArray(tokens) ? tokens[0] : (tokens || ""));
                        if (parsed && parsed.tile != null) {
                            tile = parsed.tile; // loot tokens leave air
                            if (isDoor(tile)) tile = withModifier(tile, doorOpenAt(seed, tcx, tri) ? "doorOpen" : "doorClosed");
                        }
                    }
                    grid[(sr + r) * CTW + (sc + c)] = tile;
                }
            }
        }

        // Spawn structures: stamp any whose chunk is this one.
        for (const e of spawnPoints()) {
            if (e.cx === cx && e.cy === cy) stampSpawnStructure(grid, e, t0x, t0y);
        }

        // The canonical spawn also gets a starting gate a few tiles to its left.
        if (cx === SPAWN_CX && cy === SPAWN_CY) {
            const padRow = tileY(SPAWN.y + 2 * TILE) - t0y;
            const padCol = tileX(SPAWN.x) - t0x;
            const gateCol = padCol - 9;
            for (let c = gateCol - 1; c <= padCol; c++) {
                if (c < 0 || c >= CTW) continue;
                if (padRow >= 0 && padRow < CTH) grid[padRow * CTW + c] = BIOME_BASE;
                for (let up = 1; up <= 4; up++) { const rr = padRow - up; if (rr >= 0 && rr < CTH) grid[rr * CTW + c] = 0; }
            }
            if (gateCol >= 0 && gateCol < CTW) {
                const woodWall = parseToken("tile:wood").tile;
                const woodDoor = parseToken("tile:wood mod:doorClosed").tile;
                for (let up = 1; up <= 3; up++) {
                    const rr = padRow - up;
                    if (rr >= 0 && rr < CTH) grid[rr * CTW + gateCol] = up <= 2 ? woodDoor : woodWall;
                }
            }
        }

        const built = { grid, feature, structure };
        gridCache.set(gkey, built);
        return built;
    };

    // --- the public tile query ---
    const tileAt = (tcx, tri) => {
        if (bounded && tri * TILE >= VOID_Y) return AIR;
        const cx = chunkOfTileX(tcx), cy = chunkOfTileY(tri);
        const { grid } = generateGrid(cx, cy);
        const col = tcx - cx * CTW, row = tri - cy * CTH;
        return grid[row * CTW + col];
    };
    const isSolid = (tcx, tri) => isSolidTile(tileAt(tcx, tri));

    // A surface is a solid tile with air directly above — somewhere to stand, hence
    // somewhere a pickup can sit. Returned in world px (tile-top centre).
    const surfacesInChunk = (cx, cy) => {
        const { grid, feature } = generateGrid(cx, cy);
        if (feature === "void") return [];
        const out = [];
        const t0x = cx * CTW, t0y = cy * CTH;
        for (let row = 0; row < CTH; row++) {
            for (let col = 0; col < CTW; col++) {
                if (!isSolidTile(grid[row * CTW + col])) continue;
                const tcx = t0x + col, tri = t0y + row;
                if (isSolid(tcx, tri - 1)) continue; // something on top: not a surface
                out.push({ id: `${tcx},${tri}`, x: (tcx + 0.5) * TILE, y: tri * TILE });
            }
        }
        return out;
    };

    // The guaranteed loot a structure places, in world px, each with a forced item.
    const structureSitesInChunk = (cx, cy) => {
        const { structure } = generateGrid(cx, cy);
        if (!structure) return [];
        const { kind, col: sc, row: sr, def } = structure;
        const out = [];
        const floorTri = cy * CTH + sr + def.h - 1;
        let wIdx = 0;
        for (let r = 0; r < def.h; r++) {
            for (let c = 0; c < def.w; c++) {
                const tokens = def.mapping[def.rows[r][c]];
                if (!tokens) continue;
                const token = Array.isArray(tokens) ? tokens[wIdx % tokens.length] : tokens;
                const parsed = parseToken(token);
                if (!parsed || !parsed.loot) continue;
                if (Array.isArray(tokens)) wIdx++;
                const tcx = cx * CTW + sc + c;
                out.push({ id: `${cx},${cy}:${kind}:${c}`, x: (tcx + 0.5) * TILE, y: floorTri * TILE, item: parsed.loot });
            }
        }
        return out;
    };

    const chunkInfo = (cx, cy) => {
        const { grid, feature, structure } = generateGrid(cx, cy);
        let solid = 0;
        for (let i = 0; i < grid.length; i++) if (isSolidTile(grid[i])) solid++;
        return { feature, structure: structure ? structure.kind : null, solid, total: grid.length, fill: solid / grid.length };
    };

    return {
        seed, name,
        tileAt, isSolid,
        // What terrain.js fills a built/solidified tile with (generator-appropriate base).
        fillTileAt: (tcx, tri) => strat.fillTileAt(g, tcx, tri),
        biomeBaseAt, biomeIndexAt,
        surfacesInChunk, structureSitesInChunk, spawnPoints, chunkInfo,
    };
}

// --- module wrappers over a cached default generator -------------------------
// Back-compat for the many callers/tests that ask by (seed, …): they get the "original"
// generator for that seed, created once and reused. Worlds that want a specific generator
// build their own with createGenerator (via createTerrain).
const genCache = new Map();
export function generatorFor(seed, name = "original", bounded = true) {
    const k = name + ":" + (bounded ? "b" : "u") + ":" + seed;
    let gen = genCache.get(k);
    if (!gen) { gen = createGenerator(seed, name, { bounded }); genCache.set(k, gen); }
    return gen;
}

export const tileAt = (seed, tcx, tri) => generatorFor(seed).tileAt(tcx, tri);
export const isSolid = (seed, tcx, tri) => generatorFor(seed).isSolid(tcx, tri);
export const surfacesInChunk = (seed, cx, cy) => generatorFor(seed).surfacesInChunk(cx, cy);
export const structureSitesInChunk = (seed, cx, cy) => generatorFor(seed).structureSitesInChunk(cx, cy);
export const spawnPoints = (seed) => generatorFor(seed).spawnPoints();
export const biomeBaseAt = (seed, tcx, tri) => generatorFor(seed).biomeBaseAt(tcx, tri);
export const biomeIndexAt = (seed, tcx, tri) => generatorFor(seed).biomeIndexAt(tcx, tri);
export const chunkInfo = (seed, cx, cy) => generatorFor(seed).chunkInfo(cx, cy);
